import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axiosInstance from "../axiosinstance";
import Loader from "../Components/Loader";

const toKey = (value) => String(value ?? "").trim();
const toLower = (value) => String(value || "").trim().toLowerCase();
const hasValue = (value) => toKey(value) !== "";
const CONTENT_FONT_SIZE = "14px";

const parseTimestamp = (value) => {
  if (!value) return 0;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortByLatest = (items) =>
  [...items].sort(
    (a, b) =>
      parseTimestamp(b?.updated_at || b?.created_at) -
      parseTimestamp(a?.updated_at || a?.created_at),
  );

const inferEntityType = (...values) => {
  const text = values.map(toLower).join(" ");
  if (text.includes("flange")) return "flange";
  if (text.includes("equipment")) return "equipment";
  return "";
};

const getTicketsBuckets = (data) => {
  const assignedTickets = Array.isArray(data?.assigned?.tickets) ? data.assigned.tickets : [];
  const createdTickets = Array.isArray(data?.created?.tickets) ? data.created.tickets : [];
  return { assignedTickets, createdTickets };
};

const mergeTicketsAssignedFirst = (assignedTickets, createdTickets) => {
  const merged = [];
  const seen = new Set();

  [...assignedTickets, ...createdTickets].forEach((ticket, index) => {
    const ticketIdKey = toKey(ticket?.ticket_id);
    const fallbackKey = `${toKey(ticket?.reference_id)}::${toKey(ticket?.type)}::${toKey(ticket?.ticket_type)}::${index}`;
    const uniqueKey = ticketIdKey || fallbackKey;

    if (seen.has(uniqueKey)) return;
    seen.add(uniqueKey);
    merged.push(ticket);
  });

  return merged;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const parts = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);

  const getPart = (type) => parts.find((part) => part.type === type)?.value || "";
  const month = getPart("month");
  const day = getPart("day");
  const year = getPart("year");
  const hour = getPart("hour");
  const minute = getPart("minute");
  const dayPeriod = getPart("dayPeriod").toUpperCase();

  if (!month || !day || !year || !hour || !minute || !dayPeriod) return "—";
  return `${month} ${day}, ${year}, ${hour}:${minute}${dayPeriod}`;
};

const toTitleCase = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getStatusChipConfig = (status) => {
  const normalized = toLower(status);

  if (normalized.includes("resolved")) {
    return { label: "Resolved", bg: "#e9f9ef", color: "#136f3a", border: "#bde9c9" };
  }
  if (normalized.includes("closed")) {
    return { label: "Closed", bg: "#f1f5f9", color: "#334155", border: "#cbd5e1" };
  }
  if (normalized.includes("progress")) {
    return { label: "In Progress", bg: "#fff5e8", color: "#b45309", border: "#fed7aa" };
  }
  if (normalized.includes("open")) {
    return { label: "Open", bg: "#edf2ff", color: "#4338ca", border: "#c7d2fe" };
  }

  return {
    label: toTitleCase(status || "Unknown"),
    bg: "#f8fafc",
    color: "#475569",
    border: "#e2e8f0",
  };
};

const getPriorityChipConfig = (priority) => {
  const normalized = toLower(priority);

  if (normalized.includes("critical")) {
    return { label: "Critical", bg: "#fde8e8", color: "#9f1239", border: "#fecaca" };
  }
  if (normalized.includes("high")) {
    return { label: "High", bg: "#fff1eb", color: "#c2410c", border: "#fed7c3" };
  }
  if (normalized.includes("medium")) {
    return { label: "Medium", bg: "#fffbeb", color: "#a16207", border: "#fde68a" };
  }
  if (normalized.includes("low")) {
    return { label: "Low", bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" };
  }

  return {
    label: toTitleCase(priority || "Not Set"),
    bg: "#f8fafc",
    color: "#475569",
    border: "#e2e8f0",
  };
};

const getTagChipSx = () => ({
  borderRadius: "999px",
  height: 26,
  fontWeight: 400,
  fontSize: CONTENT_FONT_SIZE,
  fontFamily: "inherit",
  border: "1px solid #ead2d5",
  backgroundColor: "#fff6f7",
  color: "#9f1239",
  maxWidth: "100%",
  "& .MuiChip-label": {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    px: 1,
  },
});

const COLUMN_WIDTHS = {
  ticketType: "200px",
  type: "130px",
  priority: "110px",
  status: "120px",
  remark: "auto",
  createdAt: "220px",
  updatedAt: "220px",
};

const headerCellBaseSx = {
  fontWeight: 700,
  fontSize: "0.78rem",
  color: "#6b7280",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  backgroundColor: "#fbfcfe",
  borderBottom: "1px solid #edf2f7",
  py: 1.25,
};

const findInBucket = (tickets, context) => {
  const normalizedTickets = Array.isArray(tickets) ? tickets : [];
  if (!normalizedTickets.length) return null;

  if (hasValue(context.ticketId)) {
    const exact = normalizedTickets.find(
      (ticket) => toKey(ticket?.ticket_id) === toKey(context.ticketId),
    );
    if (exact) return exact;
  }

  const referenceId = toKey(context.referenceId);
  if (referenceId) {
    const byReference = normalizedTickets.filter(
      (ticket) => toKey(ticket?.reference_id) === referenceId,
    );

    if (byReference.length > 0) {
      const wantedType = inferEntityType(context.type, context.ticketType, context.referenceType);
      if (wantedType) {
        const byReferenceAndType = byReference.filter((ticket) => {
          const ticketType = inferEntityType(ticket?.type, ticket?.ticket_type);
          return ticketType === wantedType;
        });
        if (byReferenceAndType.length > 0) {
          return sortByLatest(byReferenceAndType)[0];
        }
      }
      return sortByLatest(byReference)[0];
    }
  }

  return null;
};

const findRelevantTicket = ({ assignedTickets, createdTickets }, context) => {
  const matchedAssigned = findInBucket(assignedTickets, context);
  if (matchedAssigned) return matchedAssigned;

  const matchedCreated = findInBucket(createdTickets, context);
  if (matchedCreated) return matchedCreated;

  return null;
};

function TicketNotificationDetails({ isSidebarExpanded }) {
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [highlightedTicketId, setHighlightedTicketId] = useState("");

  const matchContext = useMemo(
    () => ({
      ticketId: searchParams.get("ticket_id") || "",
      referenceId: searchParams.get("reference_id") || "",
      type: searchParams.get("type") || "",
      ticketType: searchParams.get("ticket_type") || "",
      referenceType: searchParams.get("reference_type") || "",
    }),
    [searchParams],
  );

  useEffect(() => {
    let active = true;

    const fetchUserTickets = async () => {
      setLoading(true);
      setError("");

      const debugQueryParams = {
        ticket_id: matchContext.ticketId,
        reference_id: matchContext.referenceId,
        type: matchContext.type,
        ticket_type: matchContext.ticketType,
        reference_type: matchContext.referenceType,
      };
      console.log("[TicketNotificationDetails] Query params:", debugQueryParams);
      console.log(
        "[TicketNotificationDetails] authKey present:",
        Boolean(localStorage.getItem("authKey")),
      );

      try {
        const response = await axiosInstance.get("/api/user/tickets");
        if (!active) return;

        console.log("[TicketNotificationDetails] /api/user/tickets raw response:", response);
        console.log("[TicketNotificationDetails] /api/user/tickets data payload:", response?.data);

        const { assignedTickets, createdTickets } = getTicketsBuckets(response?.data);
        const list = mergeTicketsAssignedFirst(assignedTickets, createdTickets);

        console.log("[TicketNotificationDetails] assignedTickets.length:", assignedTickets.length);
        console.log("[TicketNotificationDetails] createdTickets.length:", createdTickets.length);
        console.log("[TicketNotificationDetails] finalRenderedTickets.length:", list.length);
        console.log("[TicketNotificationDetails] Extracted tickets:", list);
        setTickets(list);

        const matched = findRelevantTicket({ assignedTickets, createdTickets }, matchContext);
        console.log("[TicketNotificationDetails] Matched ticket:", matched);
        setHighlightedTicketId(matched?.ticket_id ? toKey(matched.ticket_id) : "");
      } catch (err) {
        if (!active) return;
        console.error("[TicketNotificationDetails] Failed to fetch user tickets:", err);
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Unable to load tickets right now.",
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchUserTickets();
    return () => {
      active = false;
    };
  }, [matchContext]);

  useEffect(() => {
    if (!highlightedTicketId || loading) return;
    const element = document.getElementById(`user-ticket-${highlightedTicketId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightedTicketId, loading]);

  return (
    <div>
      <Box
        className={`table-container ${isSidebarExpanded ? "expanded" : "collapsed"}`}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 104px)",
          minHeight: 0,
          gap: 1.4,
          fontFamily: "inherit",
        }}
        style={{ padding: "14px 20px 6px" }}
      >
        <Box
          sx={{
            background: "linear-gradient(180deg, #fffafa 0%, #ffffff 100%)",
            border: "1px solid #f1d9dc",
            borderRadius: "16px",
            px: { xs: 2, md: 2.8 },
            py: { xs: 1.8, md: 2.2 },
            boxShadow: "0 10px 24px rgba(159, 18, 57, 0.05)",
          }}
        >
          <Typography className="page-title" sx={{ margin: 0 }}>
            My Tickets
          </Typography>
          <Divider sx={{ mt: 1.4, borderColor: "#f4e4e6" }} />
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            border: "1px solid #e8edf3",
            borderRadius: "16px",
            backgroundColor: "#fff",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 12px 26px rgba(15, 23, 42, 0.06)",
          }}
        >
          {loading ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Loader />
            </Box>
          ) : error ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : (
            <>
              {!highlightedTicketId &&
              (hasValue(matchContext.ticketId) || hasValue(matchContext.referenceId)) ? (
                <Box sx={{ p: 2, pb: 0 }}>
                  <Alert severity="info">
                    Showing your ticket list. Exact notification match was not found.
                  </Alert>
                </Box>
              ) : null}

              <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, boxShadow: "none" }}>
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "100%",
                    minWidth: 1240,
                  }}
                >
                  <colgroup>
                    <col style={{ width: COLUMN_WIDTHS.ticketType }} />
                    <col style={{ width: COLUMN_WIDTHS.type }} />
                    <col style={{ width: COLUMN_WIDTHS.priority }} />
                    <col style={{ width: COLUMN_WIDTHS.status }} />
                    <col style={{ width: COLUMN_WIDTHS.remark }} />
                    <col style={{ width: COLUMN_WIDTHS.createdAt }} />
                    <col style={{ width: COLUMN_WIDTHS.updatedAt }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellBaseSx, width: COLUMN_WIDTHS.ticketType, textAlign: "center" }}>
                        Ticket Type
                      </TableCell>
                      <TableCell sx={{ ...headerCellBaseSx, width: COLUMN_WIDTHS.type, textAlign: "center" }}>
                        Type
                      </TableCell>
                      <TableCell sx={{ ...headerCellBaseSx, width: COLUMN_WIDTHS.priority }}>
                        Priority
                      </TableCell>
                      <TableCell sx={{ ...headerCellBaseSx, width: COLUMN_WIDTHS.status, textAlign: "center" }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ ...headerCellBaseSx, width: COLUMN_WIDTHS.remark, textAlign: "center" }}>
                        Remark
                      </TableCell>
                      <TableCell
                        sx={{
                          ...headerCellBaseSx,
                          width: COLUMN_WIDTHS.createdAt,
                          minWidth: COLUMN_WIDTHS.createdAt,
                          whiteSpace: "nowrap",
                          textAlign: "center",
                        }}
                      >
                        Created At
                      </TableCell>
                      <TableCell
                        sx={{
                          ...headerCellBaseSx,
                          width: COLUMN_WIDTHS.updatedAt,
                          minWidth: COLUMN_WIDTHS.updatedAt,
                          whiteSpace: "nowrap",
                          textAlign: "center",
                        }}
                      >
                        Updated At
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: "#6b7280" }}>
                          No tickets available
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortByLatest(tickets).map((ticket) => {
                        const ticketIdKey = toKey(ticket?.ticket_id);
                        const isHighlighted = ticketIdKey !== "" && ticketIdKey === highlightedTicketId;
                        const hasTicketType = hasValue(ticket?.ticket_type);
                        const statusChip = getStatusChipConfig(ticket?.status);
                        const hasPriority = hasValue(ticket?.priority);
                        const priorityChip = hasPriority
                          ? getPriorityChipConfig(ticket?.priority)
                          : null;

                        return (
                          <TableRow
                            key={`${ticketIdKey || "ticket"}-${ticket?.reference_id || ""}`}
                            id={ticketIdKey ? `user-ticket-${ticketIdKey}` : undefined}
                            sx={{
                              backgroundColor: isHighlighted ? "#fff7f8" : "#ffffff",
                              transition: "background-color 0.2s ease",
                              "&:hover": {
                                backgroundColor: isHighlighted ? "#fff3f5" : "#f8fafc",
                              },
                              "& td": {
                                borderBottom: "1px solid #eef2f7",
                                py: 1.4,
                                verticalAlign: "top",
                                fontSize: CONTENT_FONT_SIZE,
                                fontFamily: "inherit",
                                fontWeight: 400,
                              },
                              "& td:first-of-type": {
                                borderLeft: isHighlighted
                                  ? "4px solid #dc2626"
                                  : "4px solid transparent",
                              },
                            }}
                          >
                            <TableCell sx={{ width: COLUMN_WIDTHS.ticketType, pr: 2, overflow: "hidden" }}>
                              {hasTicketType ? (
                                <Chip
                                  size="small"
                                  label={toTitleCase(ticket?.ticket_type)}
                                  sx={getTagChipSx()}
                                />
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: CONTENT_FONT_SIZE,
                                    color: "#64748b",
                                    fontFamily: "inherit",
                                    textAlign: "center",
                                    fontWeight: 400,
                                  }}
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ width: COLUMN_WIDTHS.type, pr: 1.5, overflow: "hidden" }}>
                              <Chip
                                size="small"
                                label={toTitleCase(ticket?.type || "—")}
                                sx={{
                                  ...getTagChipSx(),
                                  backgroundColor: "#f8fafc",
                                  borderColor: "#dbe4ee",
                                  color: "#334155",
                                  maxWidth: "100%",
                                }}
                              />
                            </TableCell>

                            <TableCell sx={{ width: COLUMN_WIDTHS.priority }}>
                              {hasPriority ? (
                                <Chip
                                  size="small"
                                  label={priorityChip.label}
                                  sx={{
                                    borderRadius: "999px",
                                    height: 26,
                                    fontSize: CONTENT_FONT_SIZE,
                                    fontFamily: "inherit",
                                    fontWeight: 400,
                                    border: `1px solid ${priorityChip.border}`,
                                    backgroundColor: priorityChip.bg,
                                    color: priorityChip.color,
                                    "& .MuiChip-label": { px: 1 },
                                  }}
                                />
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: CONTENT_FONT_SIZE,
                                    color: "#64748b",
                                    fontFamily: "inherit",
                                    textAlign: "center",
                                    fontWeight: 400,
                                  }}
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ width: COLUMN_WIDTHS.status }}>
                              <Chip
                                size="small"
                                label={statusChip.label}
                                sx={{
                                  borderRadius: "999px",
                                  height: 26,
                                  fontSize: CONTENT_FONT_SIZE,
                                  fontFamily: "inherit",
                                  fontWeight: 500,
                                  border: `1px solid ${statusChip.border}`,
                                  backgroundColor: statusChip.bg,
                                  color: statusChip.color,
                                  "& .MuiChip-label": { px: 1 },
                                }}
                              />
                            </TableCell>

                            <TableCell sx={{ width: COLUMN_WIDTHS.remark }}>
                              <Typography
                                sx={{
                                  fontSize: CONTENT_FONT_SIZE,
                                  color: "#334155",
                                  fontWeight: 400,
                                  lineHeight: 1.45,
                                  fontFamily: "inherit",
                                  whiteSpace: "normal",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                                title={ticket?.remark || "—"}
                              >
                                {ticket?.remark || "—"}
                              </Typography>
                            </TableCell>

                            <TableCell
                              sx={{
                                width: COLUMN_WIDTHS.createdAt,
                                minWidth: COLUMN_WIDTHS.createdAt,
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: CONTENT_FONT_SIZE,
                                  color: "#64748b",
                                  fontWeight: 400,
                                  fontFamily: "inherit",
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {formatDateTime(ticket?.created_at)}
                              </Typography>
                            </TableCell>

                            <TableCell
                              sx={{
                                width: COLUMN_WIDTHS.updatedAt,
                                minWidth: COLUMN_WIDTHS.updatedAt,
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: CONTENT_FONT_SIZE,
                                  color: "#64748b",
                                  fontWeight: 400,
                                  fontFamily: "inherit",
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {formatDateTime(ticket?.updated_at)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Box>
    </div>
  );
}

export default TicketNotificationDetails;
